/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-04-23 12:01:29
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-03-12 16:31:56
 */
import React, { useCallback } from 'react'
import { debounce } from '../../../../utils/utils'
import sensors from '../../../../utils/sensors'
import CONFIG from '../../../../constants/config'

export function useImageHandlers({
  state,
  setState,
  imgRef,
  mouseDown,
  downPosition,
  movement,
  offsetX,
  offsetY,
  props,
  setScaleChanged
}) {
  // 埋点上报函数
  const handleReportSA = useCallback(
    (
      clickItem: string,
      folderID?: string,
      folderName?: string,
      content?: string,
      string1?: string,
      imgUrl?: string,
      number1?: number
    ) => {
      const { activeIndex } = state
      const { myRole, groupId, photos } = props
      const properties = {
        role: CONFIG.saKeywords.roles[myRole],
        groupID: groupId
      }
      const photo = photos[activeIndex]
      const params: any = {
        clickItem,
        ...properties,
        folderID,
        folderName,
        fileType: CONFIG.saKeywords.fileTypes[photo?.mediaType],
        sourceType: CONFIG.saKeywords.sourceTypes[photo?.sourceType],
        content,
        string1,
        PassiveUserid: photo?.userID || '',
        fromPlace: props.fromPlace,
        track_sign: 'aworkgroup.b4312.action.web_workgroup_preview_page_click',
        requirement_id: 156
      }
      if (clickItem === 'previous' || clickItem === 'next') {
        params.imageUrl = imgUrl
        params.number1 = number1
      }
      sensors.track('web_workgroup_preview_page_click', params)
    },
    [
      state.activeIndex,
      props.myRole,
      props.groupId,
      props.photos,
      props.fromPlace
    ]
  )

  // 照片清晰度埋点函数
  const handlePhotoClearSA = useCallback(
    (actionType: string, isSever: boolean = false) => {
      if (state.isComparisonMode || isSever) {
        sensors.track('web_get_action_photo_be_clear_page', {
          actionType,
          activeIndex: state.activeIndex === 0 ? 'original' : 'newClearPhoto'
        })
      }
    },
    [state.isComparisonMode, state.activeIndex]
  )

  // 重置移动参数
  const handleResetMoveArgs = useCallback(() => {
    mouseDown.current = false
    downPosition.current = null
    movement.current = { x: 0, y: 0 }
    offsetX.current = 0
    offsetY.current = 0
  }, [mouseDown, downPosition, movement, offsetX, offsetY])

  // 信息面板展示切换
  const handleInfo = useCallback(() => {
    handleReportSA('sidebar')
    handlePhotoClearSA('sidebar')
    const { isHideInfo } = state
    setState((prevState) => ({
      ...prevState,
      isHideInfo: !isHideInfo
    }))
  }, [state.isHideInfo, handleReportSA, handlePhotoClearSA, setState])

  // 旋转图片
  const handleRotate = useCallback(
    (deg: number) => {
      const { rotate } = state
      setState((prevState) => ({
        ...prevState,
        rotate: rotate + deg
      }))
      handleResetMoveArgs()
    },
    [state.rotate, setState, handleResetMoveArgs]
  )

  // 鼠标事件处理
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.persist()
      mouseDown.current = true
      downPosition.current = { x: e.clientX, y: e.clientY }
    },
    [mouseDown, downPosition]
  )

  const handleMouseUpOrOut = useCallback(() => {
    mouseDown.current = false
    downPosition.current = null
    movement.current = {
      x: offsetX.current,
      y: offsetY.current
    }
  }, [mouseDown, downPosition, movement, offsetX, offsetY])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const { scale, rotate } = state
      if (scale !== 100 && imgRef.current) {
        e.persist()
        if (mouseDown.current && downPosition.current) {
          offsetX.current =
            movement.current.x + e.clientX - downPosition.current.x
          offsetY.current =
            movement.current.y + e.clientY - downPosition.current.y
          imgRef.current.style.transform = `translate3d(calc(-50% + ${
            offsetX.current
          }px), calc(-50% + ${offsetY.current}px), 0) scale(${
            scale / 100
          }) rotate(${rotate}deg)`
        }
      }
    },
    [
      state.scale,
      state.rotate,
      imgRef,
      mouseDown,
      downPosition,
      movement,
      offsetX,
      offsetY
    ]
  )

  // 缩放功能
  const handleZoomOut = useCallback(() => {
    const { scale } = state
    setState((prevState) => ({
      ...prevState,
      scale: scale > 80 ? scale - 20 : scale
    }))
    handleResetMoveArgs()
    handleReportSA('zoomOut')
    handlePhotoClearSA('zoomOut')
  }, [
    state.scale,
    setState,
    handleResetMoveArgs,
    handleReportSA,
    handlePhotoClearSA
  ])

  const handleZoomIn = useCallback(() => {
    const { scale } = state
    const newScale = scale < 200 ? scale + 20 : scale

    setState((prevState) => ({
      ...prevState,
      scale: newScale
    }))

    if (scale === 100 && !state.isComparisonMode) {
      setScaleChanged(true)
    }

    handleResetMoveArgs()
    handleReportSA('zoomIn')
    handlePhotoClearSA('zoomIn')
  }, [
    state.scale,
    state.isComparisonMode,
    setState,
    setScaleChanged,
    handleResetMoveArgs,
    handleReportSA,
    handlePhotoClearSA
  ])

  // 鼠标滚轮缩放
  const handleImageMouseWheel = useCallback(
    debounce((deltaY: number) => {
      if (state.scale > 100 && state.scale < 200) {
        handleReportSA(deltaY > 0 ? 'zoomOutRoll' : 'zoomInRoll')
        handlePhotoClearSA(deltaY > 0 ? 'zoomOutRoll' : 'zoomInRoll')
      }

      if (deltaY > 0) {
        handleZoomOut()
      } else {
        handleZoomIn()
      }
    }, 10),
    [
      state.scale,
      handleReportSA,
      handlePhotoClearSA,
      handleZoomOut,
      handleZoomIn
    ]
  )

  // 画廊滚动处理
  const handleMouseWheel = useCallback((e: React.WheelEvent) => {
    e.persist()
    const gallery = document.getElementsByClassName('gallery')[0] as HTMLElement
    if (gallery) {
      requestAnimationFrame(() => {
        gallery.scrollLeft += e.nativeEvent.deltaY * 4
      })
    }
  }, [])

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      e.persist()
      const { clientWidth, scrollLeft, scrollWidth } = e.target as HTMLElement
      if (
        props.onFetchMore &&
        scrollWidth > clientWidth &&
        scrollWidth - clientWidth - scrollLeft < 100
      ) {
        props.onFetchMore()
      }
    },
    [props]
  )

  return {
    handleReportSA,
    handlePhotoClearSA,
    handleResetMoveArgs,
    handleInfo,
    handleRotate,
    handleMouseDown,
    handleMouseUpOrOut,
    handleMouseMove,
    handleZoomOut,
    handleZoomIn,
    handleImageMouseWheel,
    handleMouseWheel,
    onScroll
  }
}
